import { AddShoppingCart, MenuBook, SettingsOutlined } from "@mui/icons-material";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { decode } from "html-entities";
import React from "react";
import CatvanteLogo from "../SharedComponents/images/catvante-logo.png";
import { IInstrument } from "./controller";

const instrumentImagesById: Record<number, string> = {
  27: "/images/instruments/escan.png",
  96: "/images/instruments/eiapsi-cat.png",
  129: "/images/instruments/anamnese-carreira.png",
  15: "/images/instruments/ssperform.png",
  17: "/images/instruments/ssperform.png",
  5: "/images/instruments/eaip.png",
  6: "/images/instruments/eaic.png",
  7: "/images/instruments/eaadc.png",
  131: "/images/instruments/tipa.png",
};

const instrumentLinksById: Record<number, string> = {
  27: "https://catvante.com.br/produtos/escan",
  96: "https://catvante.com.br/produtos/eiapsi-cat",
  129: "https://catvante.com.br/produtos/anamnese-de-carreira",
  15: "https://catvante.com.br/produtos/ssperform",
  17: "https://catvante.com.br/produtos/ssperform",
  131: "https://catvante.com.br/produtos/tipa",
};

interface State {
  anchorEl: null | HTMLElement;
}

interface Props {
  instrument: IInstrument;
  delegate: ManagedInstrumentRowDelegate;
  canManageDiscounts: boolean;
}

export interface ManagedInstrumentRowDelegate {
  didSelectInstrument(instrument: IInstrument): void;
  didRequestInstrumentDiscounts(instrument: IInstrument): void;
  didRequestInstrumentDiscountEdit(instrument: IInstrument): void;
}

class ManagedInstrumentRow extends React.Component<Props, State> {
  private manuals: { [key: number]: string } = {
    27: "/manuals/escan.pdf",
    131: "/manuals/tipa.pdf",
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      anchorEl: null,
    };
  }

  handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleMenuItemClick = (action: () => void) => {
    this.handleMenuClose();
    action();
  };

  didClickSelect() {
    this.props.delegate.didSelectInstrument(this.props.instrument);
  }

  handleDiscountClick = () => {
    this.props.delegate.didRequestInstrumentDiscounts(this.props.instrument);
  };

  handleManageDiscountsClick = () => {
    this.props.delegate.didRequestInstrumentDiscountEdit(this.props.instrument);
  };

  render() {
    const menuOpen = Boolean(this.state.anchorEl);
    const hasManual = this.manuals[this.props.instrument.id];
    const { instrument } = this.props;
    const hasPriceValue =
      instrument.priceInBrlCents !== null &&
      instrument.priceInBrlCents !== undefined;
    const hasDiscounts =
      Array.isArray(instrument.discountRules) &&
      instrument.discountRules.length > 0;
    const formattedPrice = hasPriceValue
      ? new Intl.NumberFormat("pt-br", {
          style: "currency",
          currency: "BRL",
        }).format(instrument.priceInBrlCents / 100)
      : "-";
    const canShowDiscounts = hasPriceValue && hasDiscounts;
    const canBuyInstrument = Boolean(this.props.instrument.priceInBrlCents);
    
    const licenseCount = !this.props.instrument.priceInBrlCents
      ? decode("&infin;")
      : this.props.instrument.quantity ?? 0;
    const normalizedTitle = (instrument.title ?? "").toLowerCase();
    const imageFromId = instrumentImagesById[instrument.id];
    const matchInstrumentResourceByTitle = (resources: Record<number, string>) => {
      if (normalizedTitle.includes("escan")) {
        return resources[27];
      }
      if (normalizedTitle.includes("eiapsi")) {
        return resources[96];
      }
      if (normalizedTitle.includes("anamnese")) {
        return resources[129];
      }
      return undefined;
    };
    const imageFromTitle = matchInstrumentResourceByTitle(instrumentImagesById);
    const instrumentImageSrc = imageFromId ?? imageFromTitle ?? CatvanteLogo;
    const linkFromId = instrumentLinksById[instrument.id];
    const linkFromTitle = matchInstrumentResourceByTitle(instrumentLinksById);
    const instrumentInfoLink = linkFromId ?? linkFromTitle;
    const imageAlt =
      instrumentImageSrc === CatvanteLogo
        ? "Logotipo Catavante"
        : instrument.title ?? "Instrumento";
    const instrumentImage = (
      <Box
        sx={{
          width: "100%",
          height: { xs: 240, sm: 260, md: 280 },
          borderRadius: 2,
          backgroundColor: grey[200],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={instrumentImageSrc}
          alt={imageAlt}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
    );

    return (
      <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex" }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: grey[50],
            display: "flex",
            flexDirection: "column",
            width: "100%",
            flexGrow: 1,
            border: "1px solid",
            borderColor: grey[200],
            transition: "background-color 0.2s ease, border-color 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(30,160,151,0.08)",
              borderColor: "rgba(30,160,151,0.35)",
            },
          }}
        >
          <Stack spacing={2} sx={{ flexGrow: 1 }}>
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              {instrumentInfoLink ? (
                <Link
                  href={instrumentInfoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  color="inherit"
                  sx={{ display: "block", borderRadius: 2 }}
                >
                  {instrumentImage}
                </Link>
              ) : (
                instrumentImage
              )}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1.5}
              >
                <Box flex={1} minWidth={0}>
                  {instrumentInfoLink && (
                    <Chip
                      component="a"
                      href={instrumentInfoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <span>Ver página do produto</span>
                          <LaunchIcon fontSize="inherit" />
                        </Box>
                      }
                      variant="outlined"
                      color="primary"
                      size="small"
                      clickable
                      sx={{ mb: 0.5, alignSelf: "flex-start" }}
                    />
                  )}
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {this.props.instrument.title}
                  </Typography>

                  {this.props.instrument.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {this.props.instrument.description}
                    </Typography>
                  )}
                </Box>

                <Tooltip title="Mais opções">
                  <span>
                    <IconButton
                      aria-label="actions"
                      size="medium"
                      sx={{
                        backgroundColor: "grey.200",
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "grey.300",
                        },
                      }}
                      onClick={this.handleMenuClick}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`Licenças: ${licenseCount}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Stack>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1.5}
            >
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography variant="subtitle2" color="text.secondary">
                  Preço
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {formattedPrice}
                </Typography>
                {canShowDiscounts && (
                  <Tooltip title="Ver descontos">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={this.handleDiscountClick}
                      sx={{ p: 0.5 }}
                      aria-label="ver descontos"
                    >
                      <DiscountOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Tooltip title="Comprar instrumento">
                <span>
                  <IconButton
                    aria-label="comprar instrumento"
                    size="medium"
                    color="primary"
                    onClick={() => this.didClickSelect()}
                    disabled={!canBuyInstrument}
                  >
                    <AddShoppingCart />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Stack>
        </Paper>

        <Menu
          anchorEl={this.state.anchorEl}
          open={menuOpen}
          onClose={this.handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "actions-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={() =>
              this.handleMenuItemClick(() => this.didClickSelect())
            }
            disabled={!canBuyInstrument}
          >
            <ListItemIcon>
              <AddShoppingCart fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Comprar Instrumento" />
          </MenuItem>

          {canShowDiscounts && (
            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() => this.handleDiscountClick())
              }
            >
              <ListItemIcon>
                <DiscountOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Ver descontos" />
            </MenuItem>
          )}

          {this.props.canManageDiscounts && (
            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() => this.handleManageDiscountsClick())
              }
            >
              <ListItemIcon>
                <SettingsOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Configurar descontos" />
            </MenuItem>
          )}

          {hasManual && (
            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() =>
                  window.open(
                    this.manuals[this.props.instrument.id],
                    "_blank"
                  )
                )
              }
            >
              <ListItemIcon>
                <MenuBook fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Manual do Instrumento" />
            </MenuItem>
          )}
        </Menu>
      </Grid>
    );
  }
}

export default ManagedInstrumentRow;
